/**
 * Performance Check Module
 * Detects N+1 queries, over-fetching, and performance anti-patterns
 */

import type { CheckContext, CodeReviewFinding } from '../types.js';
import { generateFindingId } from '../types.js';

// N+1 Query patterns
const N_PLUS_ONE_PATTERNS = [
  {
    // Query inside foreach/for loop
    pattern: /(?:foreach|for)\s*\([^)]+\)\s*\{[^}]*(?:await\s+)?_context\.\w+\./gi,
    name: 'Query in loop',
    description: 'Database query inside a loop can cause N+1 performance issues',
  },
  {
    // Nested Select with query
    pattern: /\.Select\s*\([^)]*=>[^)]*\.(?:First|Single|Where|Any)\s*\(/gi,
    name: 'Nested query in Select',
    description: 'Nested query inside Select projection causes N+1',
  },
  {
    // Lazy loading trigger
    pattern: /(?:foreach|for)\s*\([^)]+\)\s*\{[^}]*\.\w+Navigation\./gi,
    name: 'Navigation property in loop',
    description: 'Accessing navigation property in loop triggers lazy loading',
  },
];

// Over-fetching patterns
const OVER_FETCHING_PATTERNS = [
  {
    // Multiple Include calls
    pattern: /\.Include\s*\([^)]+\)\s*\.Include\s*\([^)]+\)\s*\.Include\s*\([^)]+\)/gi,
    name: 'Triple Include',
    description: 'Too many Include calls can load excessive data',
  },
  {
    // ToList before Where
    pattern: /\.ToList(?:Async)?\s*\(\s*\)\s*\.(?:Where|Select|OrderBy)/gi,
    name: 'ToList before filtering',
    description: 'ToList() before filtering loads all records into memory',
  },
  {
    // GetAll without pagination
    pattern: /GetAll(?:Async)?\s*\(\s*\)/gi,
    name: 'GetAll without pagination',
    description: 'Fetching all records without pagination can cause memory issues',
  },
];

// Frontend performance patterns
const FRONTEND_PERF_PATTERNS = [
  {
    // Multiple fetch in useEffect
    pattern: /useEffect\s*\(\s*(?:async\s*)?\(\s*\)\s*=>\s*\{[^}]*fetch[^}]*fetch/gi,
    name: 'Multiple fetches in useEffect',
    description: 'Multiple sequential API calls in useEffect should be combined or parallelized',
  },
  {
    // Fetch without dependency array
    pattern: /useEffect\s*\(\s*(?:async\s*)?\(\s*\)\s*=>\s*\{[^}]*(?:fetch|axios|api\.)[^}]*\}\s*\)/gi,
    name: 'Fetch without dependencies',
    description: 'API call in useEffect without dependency array runs on every render',
  },
  {
    // setState in loop
    pattern: /(?:for|while|forEach)\s*\([^)]*\)[^{]*\{[^}]*set\w+\s*\(/gi,
    name: 'setState in loop',
    description: 'Calling setState in a loop causes multiple re-renders',
  },
  {
    // Large inline objects in JSX
    pattern: /style\s*=\s*\{\s*\{[^}]{100,}\}/gi,
    name: 'Large inline style object',
    description: 'Large inline objects create new references on each render',
  },
];

// Missing optimization patterns
const MISSING_OPTIMIZATION_PATTERNS = [
  {
    // No pagination in list queries
    pattern: /\.ToListAsync\s*\(\s*\)/gi,
    contextCheck: (content: string, index: number) => {
      const context = content.slice(Math.max(0, index - 200), index);
      return !context.includes('Take') && !context.includes('Skip') && !context.includes('PageSize');
    },
    name: 'List query without pagination',
    description: 'Large list queries should use pagination',
  },
  {
    // No AsNoTracking for read-only queries
    pattern: /\.(?:Where|Select|Include)[^;]*\.ToListAsync/gi,
    contextCheck: (content: string, index: number) => {
      const context = content.slice(Math.max(0, index - 300), index + 100);
      return !context.includes('AsNoTracking') && !context.includes('Update') && !context.includes('Add');
    },
    name: 'Missing AsNoTracking',
    description: 'Read-only queries should use AsNoTracking for better performance',
  },
];

export async function checkPerformance(context: CheckContext): Promise<CodeReviewFinding[]> {
  const findings: CodeReviewFinding[] = [];

  for (const file of context.files) {
    const lines = file.content.split('\n');

    // C# performance checks
    if (file.language === 'csharp') {
      // Check N+1 patterns
      for (const { pattern, name, description } of N_PLUS_ONE_PATTERNS) {
        let match;
        pattern.lastIndex = 0;
        while ((match = pattern.exec(file.content)) !== null) {
          const lineNumber = getLineNumber(file.content, match.index);
          const lineContent = lines[lineNumber - 1]?.trim() || '';

          findings.push({
            id: generateFindingId('performance'),
            category: 'performance',
            severity: 'critical',
            title: `N+1 Query: ${name}`,
            description: description,
            file: file.relativePath,
            line: lineNumber,
            code: truncateCode(lineContent),
            suggestion: 'Use Include() or batch the queries. Consider using .Select() projection to load only needed data.',
            autoFixable: false,
            references: ['https://docs.microsoft.com/en-us/ef/core/querying/related-data'],
          });
        }
      }

      // Check over-fetching patterns
      for (const { pattern, name, description } of OVER_FETCHING_PATTERNS) {
        let match;
        pattern.lastIndex = 0;
        while ((match = pattern.exec(file.content)) !== null) {
          const lineNumber = getLineNumber(file.content, match.index);
          const lineContent = lines[lineNumber - 1]?.trim() || '';

          findings.push({
            id: generateFindingId('performance'),
            category: 'performance',
            severity: 'warning',
            title: `Over-fetching: ${name}`,
            description: description,
            file: file.relativePath,
            line: lineNumber,
            code: truncateCode(lineContent),
            suggestion: 'Consider using .Select() projection to load only the fields you need, or split into multiple queries.',
            autoFixable: false,
          });
        }
      }

      // Check missing optimizations
      for (const { pattern, contextCheck, name, description } of MISSING_OPTIMIZATION_PATTERNS) {
        let match;
        pattern.lastIndex = 0;
        while ((match = pattern.exec(file.content)) !== null) {
          if (contextCheck && !contextCheck(file.content, match.index)) continue;

          const lineNumber = getLineNumber(file.content, match.index);
          const lineContent = lines[lineNumber - 1]?.trim() || '';

          // Skip if in test file
          if (file.relativePath.includes('Test')) continue;

          findings.push({
            id: generateFindingId('performance'),
            category: 'performance',
            severity: 'warning',
            title: `Performance: ${name}`,
            description: description,
            file: file.relativePath,
            line: lineNumber,
            code: truncateCode(lineContent),
            suggestion: name.includes('pagination')
              ? 'Add .Skip() and .Take() for pagination.'
              : 'Add .AsNoTracking() for read-only queries.',
            autoFixable: false,
          });
        }
      }
    }

    // Frontend performance checks
    if (['typescript', 'javascript', 'tsx', 'jsx'].includes(file.language)) {
      for (const { pattern, name, description } of FRONTEND_PERF_PATTERNS) {
        let match;
        pattern.lastIndex = 0;
        while ((match = pattern.exec(file.content)) !== null) {
          const lineNumber = getLineNumber(file.content, match.index);
          const lineContent = lines[lineNumber - 1]?.trim() || '';

          findings.push({
            id: generateFindingId('performance'),
            category: 'performance',
            severity: 'warning',
            title: `Frontend Performance: ${name}`,
            description: description,
            file: file.relativePath,
            line: lineNumber,
            code: truncateCode(lineContent),
            suggestion: getSuggestionForFrontendPerf(name),
            autoFixable: false,
          });
        }
      }

      // Check for missing React.memo/useMemo/useCallback
      checkMissingMemoization(file, lines, findings);
    }
  }

  return findings;
}

function checkMissingMemoization(
  file: { relativePath: string; content: string },
  lines: string[],
  findings: CodeReviewFinding[]
): void {
  // Check for expensive computations in render
  const expensivePatterns = [
    /\.filter\s*\([^)]+\)\.map\s*\(/gi,
    /\.reduce\s*\([^)]+\)/gi,
    /\.sort\s*\([^)]*\)/gi,
    /JSON\.parse\s*\(/gi,
  ];

  for (const pattern of expensivePatterns) {
    let match;
    pattern.lastIndex = 0;
    while ((match = pattern.exec(file.content)) !== null) {
      const lineNumber = getLineNumber(file.content, match.index);

      // Check if inside useMemo
      const contextStart = Math.max(0, match.index - 100);
      const context = file.content.slice(contextStart, match.index);
      if (context.includes('useMemo') || context.includes('useCallback')) continue;

      // Check if inside component render (simplified check)
      const beforeMatch = file.content.slice(Math.max(0, match.index - 500), match.index);
      if (beforeMatch.includes('return (') || beforeMatch.includes('return <')) {
        findings.push({
          id: generateFindingId('performance'),
          category: 'performance',
          severity: 'warning',
          title: 'Expensive Computation in Render',
          description: 'Expensive array operations in render body should be memoized.',
          file: file.relativePath,
          line: lineNumber,
          code: truncateCode(lines[lineNumber - 1]?.trim() || ''),
          suggestion: 'Wrap this computation with useMemo() to prevent recalculation on every render.',
          autoFixable: false,
        });
      }
    }
  }
}

function getSuggestionForFrontendPerf(name: string): string {
  const suggestions: Record<string, string> = {
    'Multiple fetches in useEffect': 'Use Promise.all() to parallelize requests or combine into a single API call.',
    'Fetch without dependencies': 'Add a dependency array to prevent the effect from running on every render.',
    'setState in loop': 'Batch state updates or use a reducer pattern.',
    'Large inline style object': 'Extract styles to a useMemo() hook or a constant outside the component.',
  };
  return suggestions[name] || 'Optimize this code for better performance.';
}

function getLineNumber(content: string, index: number): number {
  return content.slice(0, index).split('\n').length;
}

function truncateCode(code: string, maxLength: number = 100): string {
  if (code.length <= maxLength) return code;
  return code.slice(0, maxLength - 3) + '...';
}
